import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2, Lock, Tag } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { k: "el", l: "Earned Leave", paid: true },
  { k: "sl", l: "Sick Leave", paid: true },
  { k: "cl", l: "Casual Leave", paid: true },
  { k: "weekly_off", l: "Weekly Off", paid: true },
  { k: "lop", l: "Loss of Pay (Unpaid)", paid: false },
];
const COLORS = ["zinc", "sky", "emerald", "amber", "orange", "red", "violet", "pink", "teal", "blue"];
const COLOR_BG = {
  zinc: "bg-zinc-400", sky: "bg-sky-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
  orange: "bg-orange-500", red: "bg-red-500", violet: "bg-violet-500", pink: "bg-pink-500",
  teal: "bg-teal-500", blue: "bg-blue-500",
};
const catLabel = (k) => CATEGORIES.find((c) => c.k === k)?.l || k;
const isPaidCat = (k) => CATEGORIES.find((c) => c.k === k)?.paid;

export const LeaveTypesManagerDialog = ({ open, onOpenChange, onChanged }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ label: "", leave_category: "lop", half_day: false, color: "zinc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leave-types?include_inactive=true");
      setTypes(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!draft.label.trim()) { toast.error("Enter a name"); return; }
    setSaving(true);
    try {
      await api.post("/leave-types", draft);
      toast.success("Leave type added");
      setDraft({ label: "", leave_category: "lop", half_day: false, color: "zinc" });
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const patch = async (key, body) => {
    try {
      await api.put(`/leave-types/${key}`, body);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/leave-types/${key}`);
      toast.success("Removed");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="leave-types-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0">
          <DialogTitle className="flex items-center gap-2"><Tag className="w-4 h-4" /> Leave types</DialogTitle>
          <DialogDescription>Customize the leave types your team can request. Paid types draw from a leave balance bucket; unpaid (LOP) docks salary. System types can be renamed/hidden but not deleted.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : types.map((t) => (
            <div key={t.key} className={`flex items-center gap-2 border rounded-md px-3 py-2 ${t.active === false ? "bg-zinc-50 opacity-60" : "bg-white"} border-zinc-200`} data-testid={`leave-type-${t.key}`}>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${COLOR_BG[t.color] || "bg-zinc-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {t.label}
                  {t.is_system && <Lock className="w-3 h-3 text-zinc-400" title="System type" />}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {catLabel(t.leave_category)} · {t.is_paid ? "Paid" : "Unpaid"}{t.half_day ? " · Half day" : ""}
                </div>
              </div>
              <Switch checked={t.active !== false} onCheckedChange={(v) => patch(t.key, { active: v })} data-testid={`leave-type-active-${t.key}`} />
              {!t.is_system && (
                <button onClick={() => remove(t.key)} className="text-zinc-400 hover:text-red-600 shrink-0" data-testid={`leave-type-del-${t.key}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50/60 shrink-0 space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Add a leave type</Label>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[140px]">
              <Input placeholder="e.g. Maternity Leave" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="new-leave-type-label" />
            </div>
            <Select value={draft.leave_category} onValueChange={(v) => setDraft({ ...draft, leave_category: v })}>
              <SelectTrigger className="w-[170px]" data-testid="new-leave-type-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={draft.color} onValueChange={(v) => setDraft({ ...draft, color: v })}>
              <SelectTrigger className="w-[110px]" data-testid="new-leave-type-color"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COLORS.map((c) => <SelectItem key={c} value={c}><span className="flex items-center gap-2 capitalize"><span className={`w-2.5 h-2.5 rounded-full ${COLOR_BG[c]}`} />{c}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-zinc-600">
              <Switch checked={draft.half_day} onCheckedChange={(v) => setDraft({ ...draft, half_day: v })} data-testid="new-leave-type-halfday" />
              Half-day type
            </label>
            <Button onClick={add} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800" data-testid="add-leave-type-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add type
            </Button>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-100 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="leave-types-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
